import type { FactoriesFactory } from "@/api-client";
import { PermissionTooltip } from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getApiErrorMessage } from "@/lib/errors";
import { showErrorToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

import {
  DEFAULT_BADGE_THEME,
  type BadgeColorSlot,
  type BadgeColors,
  type BadgeTheme,
  badgeThemeColors,
  changedBadgeColors,
} from "../../lib/badgeThemes";
import { VELOCITY_PERIOD_DAYS, type VelocityPeriodDays } from "../../lib/factoryVelocityReport";
import { FactorySettingsCard } from "./FactorySettingsCard";
import { PublicBadgeThemeEditor } from "./PublicBadgeThemeEditor";

const PUBLIC_BADGE_COPY = {
  title: "Public badge",
  enabledLabel: "Public badge",
  enabledHelper: "Anyone with the link can see the share, trend, and merge rate.",
  costLabel: "Show cost per merged PR",
  costHelper: "Anyone with the link can see the cost per merged pull request on the Large and Full width badges.",
  linkLabel: "Link the badge to the public board",
  linkHelper: "A click on the badge opens the line board.",
  linkPrivateHelper: "The workspace is private. Make it public in the Visibility section above.",
  periodLabel: "Time frame",
  sizeLabel: "Size",
  snippetHelper: "Paste this snippet into a README.",
  previewAlt: "Public badge preview",
  saveError: "SuperPlane could not update the public badge.",
  permission: "You don't have permission to update workspaces.",
} as const;

const BADGE_SIZES = [
  { value: "small", label: "Small" },
  { value: "large", label: "Large" },
  { value: "wide", label: "Full width" },
] as const;

type BadgeSize = (typeof BADGE_SIZES)[number]["value"];

type SnippetFormat = "markdown" | "html";

const SNIPPET_COPY: Record<SnippetFormat, { label: string; copy: string }> = {
  markdown: { label: "Markdown", copy: "Copy Markdown" },
  html: { label: "HTML", copy: "Copy HTML" },
};

function snippetFormat(size: BadgeSize): SnippetFormat {
  return size === "wide" ? "html" : "markdown";
}

const BADGE_IMAGE_ALT = "PRs via SuperPlane";

function badgeSnippet(size: BadgeSize, imageURL: string, linkURL: string): string {
  if (!imageURL) {
    return "";
  }
  if (size === "wide") {
    return `<a href="${linkURL}"><img src="${imageURL}" alt="${BADGE_IMAGE_ALT}" width="100%"></a>`;
  }
  return `[![${BADGE_IMAGE_ALT}](${imageURL})](${linkURL})`;
}

type PublicBadgeUpdate = {
  publicBadgeEnabled?: boolean;
  publicBadgeShowCost?: boolean;
};

export function PublicBadgeSettingsSection({
  factory,
  publicBoardPath,
  canUpdate,
  permissionsLoading,
  isSaving,
  onUpdate,
}: {
  factory: FactoriesFactory;
  /** Absent while the workspace is private, which keeps the link switch off. */
  publicBoardPath?: string;
  canUpdate: boolean;
  permissionsLoading: boolean;
  isSaving: boolean;
  onUpdate: (input: PublicBadgeUpdate) => Promise<FactoriesFactory>;
}) {
  const badge = usePublicBadgeState(factory, publicBoardPath, onUpdate);
  const locked = !canUpdate || isSaving;

  return (
    <FactorySettingsCard
      title={PUBLIC_BADGE_COPY.title}
      id="factory-settings-public-badge"
      data-testid="factory-settings-public-badge"
      className="scroll-mt-8"
    >
      <div className="space-y-4">
        <BadgeSwitchRow
          label={PUBLIC_BADGE_COPY.enabledLabel}
          helper={PUBLIC_BADGE_COPY.enabledHelper}
          checked={badge.enabled}
          disabled={locked}
          canUpdate={canUpdate}
          permissionsLoading={permissionsLoading}
          testId="factory-settings-public-badge-toggle"
          onCheckedChange={(next) => badge.saveEnabled(next)}
        />
        {badge.enabled ? (
          <PublicBadgeDetails
            badge={badge}
            locked={locked}
            canUpdate={canUpdate}
            permissionsLoading={permissionsLoading}
          />
        ) : null}
      </div>
    </FactorySettingsCard>
  );
}

function usePublicBadgeState(
  factory: FactoriesFactory,
  publicBoardPath: string | undefined,
  onUpdate: (input: PublicBadgeUpdate) => Promise<FactoriesFactory>,
) {
  const [enabled, setEnabled] = useState(Boolean(factory.publicBadgeEnabled));
  const [showCost, setShowCost] = useState(Boolean(factory.publicBadgeShowCost));
  const [token, setToken] = useState(factory.publicBadgeToken ?? "");
  const [period, setPeriod] = useState<VelocityPeriodDays>(30);
  const [size, setSize] = useState<BadgeSize>("small");
  const [theme, setTheme] = useState<BadgeTheme>(DEFAULT_BADGE_THEME);
  const [colors, setColors] = useState<BadgeColors>(() => badgeThemeColors(DEFAULT_BADGE_THEME));
  const [linkToBoard, setLinkToBoard] = useState(false);
  const [previewNonce, setPreviewNonce] = useState(0);

  useEffect(() => {
    setEnabled(Boolean(factory.publicBadgeEnabled));
    setShowCost(Boolean(factory.publicBadgeShowCost));
    setToken(factory.publicBadgeToken ?? "");
  }, [factory.publicBadgeEnabled, factory.publicBadgeShowCost, factory.publicBadgeToken]);

  const origin = window.location.origin;
  const changed = changedBadgeColors(theme, colors);
  const query = new URLSearchParams({ period: String(period), size });
  if (theme !== DEFAULT_BADGE_THEME) {
    query.set("theme", theme);
  }
  for (const [slot, color] of changed) {
    query.set(slot, color.slice(1));
  }
  const imageURL = token ? `${origin}/api/v1/public/badges/${token}.svg?${query}` : "";
  const linkURL = linkToBoard && publicBoardPath ? `${origin}${publicBoardPath}` : origin;

  const save = async (input: PublicBadgeUpdate, apply: () => void, revert: () => void) => {
    apply();
    try {
      const saved = await onUpdate(input);
      if (saved.publicBadgeToken) {
        setToken(saved.publicBadgeToken);
      }
      setPreviewNonce((current) => current + 1);
    } catch (error) {
      revert();
      showErrorToast(getApiErrorMessage(error, PUBLIC_BADGE_COPY.saveError));
    }
  };

  return {
    enabled,
    showCost,
    period,
    size,
    theme,
    colors,
    customized: changed.length > 0,
    linkToBoard,
    canLinkToBoard: Boolean(publicBoardPath),
    markdown: badgeSnippet(size, imageURL, linkURL),
    previewURL: imageURL ? `${imageURL}&v=${previewNonce}` : "",
    setPeriod,
    setSize,
    setLinkToBoard,
    selectTheme: (next: BadgeTheme) => {
      setTheme(next);
      setColors(badgeThemeColors(next));
    },
    setColor: (slot: BadgeColorSlot, color: string) => {
      setColors((current) => ({ ...current, [slot]: color }));
    },
    resetColors: () => setColors(badgeThemeColors(theme)),
    saveEnabled: (next: boolean) => {
      void save(
        { publicBadgeEnabled: next },
        () => setEnabled(next),
        () => setEnabled(!next),
      );
    },
    saveShowCost: (next: boolean) => {
      void save(
        { publicBadgeShowCost: next },
        () => setShowCost(next),
        () => setShowCost(!next),
      );
    },
  };
}

type BadgeState = ReturnType<typeof usePublicBadgeState>;

function PublicBadgeDetails({
  badge,
  locked,
  canUpdate,
  permissionsLoading,
}: {
  badge: BadgeState;
  locked: boolean;
  canUpdate: boolean;
  permissionsLoading: boolean;
}) {
  return (
    <div className="space-y-4 border-t border-border pt-4">
      <BadgePreview previewURL={badge.previewURL} />
      <BadgeSelectors
        period={badge.period}
        size={badge.size}
        locked={locked}
        onPeriodChange={badge.setPeriod}
        onSizeChange={badge.setSize}
      />
      <PublicBadgeThemeEditor
        theme={badge.theme}
        colors={badge.colors}
        customized={badge.customized}
        locked={locked}
        onThemeChange={badge.selectTheme}
        onColorChange={badge.setColor}
        onReset={badge.resetColors}
      />
      <BadgeSwitchRow
        label={PUBLIC_BADGE_COPY.costLabel}
        helper={PUBLIC_BADGE_COPY.costHelper}
        checked={badge.showCost}
        disabled={locked}
        canUpdate={canUpdate}
        permissionsLoading={permissionsLoading}
        testId="factory-settings-public-badge-cost"
        onCheckedChange={badge.saveShowCost}
      />
      <BadgeSwitchRow
        label={PUBLIC_BADGE_COPY.linkLabel}
        helper={PUBLIC_BADGE_COPY.linkHelper}
        checked={badge.linkToBoard && badge.canLinkToBoard}
        disabled={locked || !badge.canLinkToBoard}
        disabledReason={badge.canLinkToBoard ? undefined : PUBLIC_BADGE_COPY.linkPrivateHelper}
        canUpdate={canUpdate}
        permissionsLoading={permissionsLoading}
        testId="factory-settings-public-badge-board-link"
        onCheckedChange={badge.setLinkToBoard}
      />
      <BadgeMarkdown
        markdown={badge.markdown}
        format={snippetFormat(badge.size)}
        locked={locked}
        canUpdate={canUpdate}
        permissionsLoading={permissionsLoading}
      />
    </div>
  );
}

function BadgeSelectors({
  period,
  size,
  locked,
  onPeriodChange,
  onSizeChange,
}: {
  period: VelocityPeriodDays;
  size: BadgeSize;
  locked: boolean;
  onPeriodChange: (period: VelocityPeriodDays) => void;
  onSizeChange: (size: BadgeSize) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="factory-settings-public-badge-period">{PUBLIC_BADGE_COPY.periodLabel}</Label>
        <Select
          value={String(period)}
          disabled={locked}
          onValueChange={(value) => {
            const next = Number(value);
            if (VELOCITY_PERIOD_DAYS.some((days) => days === next)) {
              onPeriodChange(next as VelocityPeriodDays);
            }
          }}
        >
          <SelectTrigger id="factory-settings-public-badge-period" data-testid="factory-settings-public-badge-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VELOCITY_PERIOD_DAYS.map((days) => (
              <SelectItem key={days} value={String(days)}>
                {days} days
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="factory-settings-public-badge-size">{PUBLIC_BADGE_COPY.sizeLabel}</Label>
        <Select value={size} disabled={locked} onValueChange={(value) => onSizeChange(value as BadgeSize)}>
          <SelectTrigger id="factory-settings-public-badge-size" data-testid="factory-settings-public-badge-size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BADGE_SIZES.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function BadgePreview({ previewURL }: { previewURL: string }) {
  if (!previewURL) {
    return null;
  }
  return (
    <div className="flex min-h-24 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/40 p-4">
      <img
        src={previewURL}
        alt={PUBLIC_BADGE_COPY.previewAlt}
        data-testid="factory-settings-public-badge-preview"
        className="max-w-full"
      />
    </div>
  );
}

function BadgeMarkdown({
  markdown,
  format,
  locked,
  canUpdate,
  permissionsLoading,
}: {
  markdown: string;
  format: SnippetFormat;
  locked: boolean;
  canUpdate: boolean;
  permissionsLoading: boolean;
}) {
  if (!markdown) {
    return null;
  }
  const copy = SNIPPET_COPY[format];
  return (
    <div className="space-y-2">
      <Label htmlFor="factory-settings-public-badge-markdown">{copy.label}</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id="factory-settings-public-badge-markdown"
          data-testid="factory-settings-public-badge-markdown"
          value={markdown}
          readOnly
          disabled={locked}
        />
        <PermissionTooltip allowed={canUpdate || permissionsLoading} message={PUBLIC_BADGE_COPY.permission}>
          <Button
            type="button"
            variant="outline"
            disabled={locked}
            data-testid="factory-settings-public-badge-copy"
            onClick={() => void navigator.clipboard.writeText(markdown)}
          >
            {copy.copy}
          </Button>
        </PermissionTooltip>
      </div>
      <p className="text-[12px] text-muted-foreground">{PUBLIC_BADGE_COPY.snippetHelper}</p>
    </div>
  );
}

function BadgeSwitchRow({
  label,
  helper,
  checked,
  disabled,
  disabledReason,
  canUpdate,
  permissionsLoading,
  testId,
  onCheckedChange,
}: {
  label: string;
  helper: string;
  checked: boolean;
  disabled: boolean;
  /** Why the switch is off limits. It shows on hover and dims the row. */
  disabledReason?: string;
  canUpdate: boolean;
  permissionsLoading: boolean;
  testId: string;
  onCheckedChange: (checked: boolean) => void;
}) {
  // The permission tooltip states its own reason, so only one shows at a time.
  const reason = canUpdate && disabled ? disabledReason : undefined;
  // The whole row opens the tooltip, but the switch anchors it, so the arrow
  // points at the control the reason is about.
  const [showReason, setShowReason] = useState(false);
  const control = (
    <Switch
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
      aria-label={label}
      className="mt-0.5"
    />
  );

  return (
    <div
      className="flex items-start justify-between gap-6"
      data-testid={testId}
      onMouseEnter={() => setShowReason(true)}
      onMouseLeave={() => setShowReason(false)}
    >
      <div className={cn("min-w-0 space-y-0.5", reason && "opacity-60")}>
        <p className="text-[13px] font-medium text-foreground">{label}</p>
        <p className="text-[12px] leading-5 text-muted-foreground">{helper}</p>
      </div>
      {reason ? (
        <Tooltip open={showReason} onOpenChange={setShowReason}>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-not-allowed">{control}</span>
          </TooltipTrigger>
          <TooltipContent side="top" data-testid={`${testId}-reason`}>
            {reason}
          </TooltipContent>
        </Tooltip>
      ) : (
        <PermissionTooltip allowed={canUpdate || permissionsLoading} message={PUBLIC_BADGE_COPY.permission}>
          {control}
        </PermissionTooltip>
      )}
    </div>
  );
}
